import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import axiosInstance from '../../../Config/axiosInstance';
import AddOrdersAndAdditionalPappers from './AddOrdersAndAdditionalPappers';
import EditOrdersAndAdditionalPappers from './EditOrdersAndAdditionalPappers';
import './OrdersAndAdditionalPappers.css';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

function OrdersAndAdditionalPappers() {
  const [canCreate, setCanCreate] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [canDelete, setCanDelete] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Funcție pentru formatarea datei în format românesc cu PUNCTE
  const formatDateToRomanian = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    // Schimbăm slash-ul cu punct
    return `${day}.${month}.${year}`;
  };

  //Verificarea permisiunilor
  useEffect(() => {
    const requiredPath = "/orders-additional-pappers";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    const hasCreatePermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_create === true;
    });

    setCanCreate(hasCreatePermission);

    const hasEditPermission = permissions.some((page) => {
      const pagePath = new URL(page.url).pathname;
      return pagePath === requiredPath && page.can_edit === true;
    });

    setCanEdit(hasEditPermission);

    setCanDelete(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_delete));
  }, []);

  //Maparea datelor din backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contractsResponse, suppliersResponse, contractsNumberResponse] = await Promise.all([
          axiosInstance.get('/orders-additional-pappers/'),
          axiosInstance.get('/suppliers/'),
          axiosInstance.get('/supplier-contracts/')
        ]);

        const supplierMap = suppliersResponse.data.reduce((acc, supplier) => {
          acc[supplier.id] = supplier.name;
          return acc;
        }, {});

        const contractMap = contractsNumberResponse.data.reduce((acc, contract) => {
          acc[contract.id] = contract.contract_number;
          return acc;
        }, {});

        const sortedData = contractsResponse.data.map((item) => ({
          id: item.id,
          orders_or_additional: item.orders_or_additional ? 'Order' : 'Additional',
          pmpc_contract_number: contractMap[item.pmpc_contract_number] || " ",
          order_additional_number: item.order_additional_number,
          order_additional_description: item.order_additional_description,
          supplier: supplierMap[item.supplier] || " ",
          contract_description: item.contract_description || '',
          alert_days: item.alert_days || 3,
          order_additional_date: item.order_additional_date, // Păstrăm valoarea originală pentru sortare
          status: item.status,
        })).sort((a, b) => b.id - a.id);

        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  //Functii de navigare
  const handleAddClick = () => navigate('/orders-additional-pappers/add-orders-additional-pappers');
  const handleEditClick = (row) => navigate(`/orders-additional-pappers/edit-orders-additional-pappers/${row.id}`);

  //Stergere
  const handleDeleteClick = (row) => {
    setItemToDelete(row);
    setOpenDeleteDialog(true);
  };

  //Culorile statusurilor
  const statusColors = {
    New: '#9e9e9e',       // Portocaliu
    Signed: '#4caf50',    // Verde
    Expired: '#f44336',   // Roșu
    Closed: '#ff9800',    // Gri
  };

  ////Definirea coloanelor
  const columns = [
    {
      field: 'pmpc_contract_number',
      headerName: 'Contract number',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'order_additional_number',
      headerName: 'Order/Additional paper Number',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'order_additional_description',
      headerName: 'Order/Additional paper description',
      flex: 1,
      minWidth: 280,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'supplier',
      headerName: 'Supplier',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'alert_days',
      headerName: 'Alert (Days)',
      flex: 1,
      minWidth: 70,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value}
        </div>
      )
    },
    {
      field: 'order_additional_date',
      headerName: 'Order/Additional paper Date',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {formatDateToRomanian(params.value)}
        </div>
      ),
      // Adăugăm sorting comparer pentru a sorta corect după data originală
      sortComparator: (v1, v2) => {
        const date1 = new Date(v1);
        const date2 = new Date(v2);
        return date1.getTime() - date2.getTime();
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <span
            style={{
              backgroundColor: statusColors[params.value] || '#ccc',
              color: 'white',
              padding: '5px 10px',
              borderRadius: '10px',
              width: '65px',
              maxWidth: '65px',
              minWidth: '65px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '15px'
            }}
          >
            {params.value}
          </span>
        </div>
      )
    },
  ];

  //Adaugarea coloanei de editare/stergere doar daca utilizatorul are permisiune
  if (canEdit || canDelete) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <>
          {canEdit && (
            <IconButton onClick={() => handleEditClick(params.row)} sx={{ color: '#1976d2' }}>
              <EditNoteSharpIcon />
            </IconButton>
          )}
          {canDelete && (
            <IconButton onClick={() => handleDeleteClick(params.row)} sx={{ color: '#d32f2f' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="orders-additional-pappers-page">
          <div className="orders-additional-pappers-container">
            <h2 className='title'>ORDERS AND ADDITIONAL PAPERS</h2>
            {/* Afisarea butonului doar daca utilizatorulare permisiune */}
            {canCreate && (
              <div className="add-button-container">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddClick}
                  sx={{ float: 'right', marginBottom: '40px' }}
                >
                  Add
                </Button>
              </div>
            )}
          </div>

          <div className="table-container">
            <DataGrid
              id="order-additional-table"
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">Confirm Delete</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete this record?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                sx={{
                  backgroundColor: '#e0e0e0',
                  color: '#424242',
                  '&:hover': {
                    backgroundColor: '#bdbdbd',
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await axiosInstance.delete(`/orders-additional-pappers/${itemToDelete.id}/`);
                    setRows(prevRows => prevRows.filter(r => r.id !== itemToDelete.id));
                    setOpenDeleteDialog(false);
                  } catch (error) {
                    console.error('Error deleting record:', error.response?.data || error.message);
                  }
                }}
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  }
                }}
                autoFocus
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      } />
      {/* Structura subrutelor */}
      <Route path="add-orders-additional-pappers" element={<AddOrdersAndAdditionalPappers />} />
      <Route path="edit-orders-additional-pappers/:id" element={<EditOrdersAndAdditionalPappers />} />
    </Routes>
  );
}

export default OrdersAndAdditionalPappers;